const T = ({ label, b, a, testid }) => (
  <div className="rounded-md border border-border bg-alabaster p-3" data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="mt-1 text-sm text-charcoal">{b ?? "—"} <span className="text-warmgrey">→</span> <strong>{a ?? "—"}</strong></p>
  </div>
);

const pal = (p) => (p && p.length ? p.map((x) => `${x.pallet}:${x.boxes}`).join(", ") : "—");
const rec = (r) => (r ? `${r.boxes_received} rec${r.damaged ? ` · ${r.damaged} dmg` : ""}` : "—");

const Change = ({ c, received }) => (
  <li className="text-xs text-charcoal" data-testid={`recon-review-change-${c.sku_after}`}>
    <span className="font-mono">{c.sku_before && c.sku_before !== c.sku_after ? `${c.sku_before} → ` : ""}{c.sku_after}</span>
    {c.status !== "ACTIVE" && <span className="ml-1 badge-status badge-sold">{c.status === "MERGED" ? "Merged" : "Removed"}</span>}
    {!c.line_id && <span className="ml-1 badge-status badge-available">New · {c.origin?.type === "EXCLUDED_ROW" ? `excluded row ${c.origin.row}` : c.origin?.type?.toLowerCase()}</span>}
    {" "}· declared {c.declared?.sku ? `${c.declared.sku} ${c.declared.boxes}` : "—"} · expected {c.expected_before ?? 0} → {c.expected_after}
    {received && ` · received ${rec(c.received_before)} → ${rec(c.received_after)}`} · pallets {pal(c.pallet_before)} → {pal(c.pallet_after)} · {c.reason}{c.note ? ` — ${c.note}` : ""}
  </li>
);

// ID-078: exact impact of the reconciliation, computed by the server with no writes
export const ReconcileReview = ({ review }) => {
  const received = review.status === "RECEIVED";
  const { before: b, after: a } = review.totals;
  return (
    <div className="space-y-4" data-testid="recon-review">
      {review.errors.length > 0 && (
        <div className="rounded-md border border-status-sold/40 bg-status-sold/5 p-3" data-testid="recon-review-errors">
          <p className="text-xs font-semibold text-status-sold">Cannot be applied yet</p>
          <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-charcoal">{review.errors.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <T label="Corrected expected boxes" b={b.expected_boxes} a={a.expected_boxes} testid="recon-total-expected" />
        {received && <T label="Physically received" b={b.received_boxes} a={a.received_boxes} testid="recon-total-received" />}
        <T label="Square feet" b={b.sqft.toLocaleString()} a={a.sqft.toLocaleString()} testid="recon-total-sqft" />
        <T label="Lines" b={b.lines} a={a.lines} testid="recon-total-lines" />
      </div>
      <p className="text-xs text-warmgrey" data-testid="recon-total-declared">Supplier declared {a.declared_boxes} boxes (unchanged) · pallets: {pal(a.pallets)}{a.unassigned_pallet_boxes ? ` · ${a.unassigned_pallet_boxes} boxes without a pallet` : ""}</p>
      <ul className="space-y-1">{review.changes.map((c, i) => <Change key={c.line_id || `n${i}`} c={c} received={received} />)}</ul>
      <div data-testid="recon-review-stock">
        <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Inventory impact</p>
        <ul className="mt-1 space-y-1 text-xs text-charcoal">
          {review.stock.length === 0 && <li>No stock quantities change.</li>}
          {review.stock.map((s) => (
            <li key={`${s.variant_id}-${s.location}`} data-testid={`recon-stock-${s.sku}-${s.location}`} className={s.conflict ? "text-status-sold" : ""}>
              <span className="font-mono">{s.sku}</span> · {s.location === "INCOMING" ? `incoming ${s.incoming_before} → ${s.incoming_after} (no Florida stock until received)`
                : `${s.location === "MOROCCO" ? "Morocco" : "Florida"} physical ${s.physical_before} → ${s.physical_after} · available ${s.available_before} → ${s.available_after}`}
            </li>
          ))}
        </ul>
      </div>
      {review.excluded_rows.length > 0 && (
        <p className="text-xs text-charcoal" data-testid="recon-review-excluded">
          {review.excluded_rows.map((e) => `Excluded row ${e.row} (${e.supplier_sku}): ${e.reconciled_boxes}/${e.boxes} identified — ${e.remaining_boxes < 0 ? "over-allocated" : e.status === "EXCLUDED" ? "still excluded" : e.status.toLowerCase()}`).join(" · ")}
        </p>
      )}
    </div>
  );
};
